import type { AtsLinterResult, MatchEngineResult } from "@super-resume-new/resume-engine";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from "@super-resume-new/ui/components/accordion";
import { Badge } from "@super-resume-new/ui/components/badge";
import { useAuthModal } from "@/context/auth-modal";
import { authClient } from "@/lib/auth-client";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from "@super-resume-new/ui/components/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@super-resume-new/ui/components/tooltip";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  FileCode2,
  Info,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Wand2,
  XCircle,
} from "lucide-react";
import { useState } from "react";

export interface MatchScorecardProps {
  matchId: string;
  matchResult: MatchEngineResult;
  atsLinterResult: AtsLinterResult;
  job: {
    id: string;
    title: string;
    company: string | null;
    slug: string;
  };
  extractedResumeText?: string;
  onReset: () => void;
}

export function MatchScorecard({
  matchId,
  matchResult,
  atsLinterResult,
  job: _job,
  extractedResumeText,
  onReset,
}: MatchScorecardProps) {
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();
  const { data: session } = authClient.useSession();
  const [showAtsDialog, setShowAtsDialog] = useState(false);

  const handleTailorClick = () => {
    const goToTailor = () => navigate({ to: "/resume-tailor", search: { from_match: matchId } });

    if (session?.user) {
      goToTailor();
      return;
    }

    // Sign up here on the job page rather than sending anonymous visitors to
    // the tailor and prompting them only after they fill the form.
    openAuthModal({
      defaultTab: "signup",
      title: "Create your free account",
      description: "Get 3 free AI rewrites. No credit card needed.",
      // Social sign-in leaves the page, so it must return straight to the tailor.
      callbackURL: `/resume-tailor?from_match=${encodeURIComponent(matchId)}`,
      onSuccess: goToTailor,
    });
  };

  const { band, score, missing, missingNiceToHave, matched, matchedDetails, stats } = matchResult;

  const bandTheme = {
    strong: {
      bg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-500",
      pill: "bg-emerald-500 text-white",
      badge: "bg-emerald-500/20 text-emerald-500 border-emerald-500/40",
      title: "Strong Match",
      desc: "Your resume covers most core requirements for this role. You are in a great position to interview.",
    },
    possible: {
      bg: "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400",
      pill: "bg-amber-500 text-white",
      badge: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/40",
      title: "Possible Match",
      desc: "You match several qualifications, but key must-have keywords are missing from your resume.",
    },
    weak: {
      bg: "bg-destructive/10 border-destructive/30 text-destructive",
      pill: "bg-destructive text-white",
      badge: "bg-destructive/15 text-destructive border-destructive/30",
      title: "Weak Match",
      desc: "Critical must-have skills are missing. Your resume is at high risk of ATS rejection.",
    },
  }[band];

  const matchedSkillsList = matchedDetails.filter((d) => d.matched);

  return (
    <div className="flex flex-col gap-5 rounded-xl border border-border bg-card p-6 shadow-sm">
      {/* Top Banner: Score & Band */}
      <div
        className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-xl border p-5 ${bandTheme.bg}`}
      >
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center justify-center rounded-xl bg-background/90 p-3 shadow-sm border border-border text-center min-w-[72px]">
            <span className="text-2xl font-semibold text-foreground">{score}%</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Match
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-semibold">{bandTheme.title}</span>
              <Badge
                variant="secondary"
                className={`text-xs capitalize font-semibold ${bandTheme.badge}`}
              >
                {stats.mustHaveMatched}/{stats.mustHaveTotal} Must-Haves
              </Badge>
              {stats.synonymMatchesCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/40 font-medium"
                >
                  <AlertTriangle
                    className="mr-1.5 inline size-3.5 align-[-0.15em]"
                    aria-hidden="true"
                  />
                  {stats.synonymMatchesCount} related keyword matches
                </Badge>
              )}
            </div>
            <p className="mt-1 text-xs leading-relaxed opacity-90 max-w-md">{bandTheme.desc}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Tooltip>
            <TooltipTrigger
              type="button"
              className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-background/60 shrink-0 cursor-pointer"
              aria-label="About Match Score"
            >
              <Info className="size-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm p-2">
              <p className="font-semibold text-foreground mb-1">Heuristic Based ATS Matcher</p>
              <p className="text-muted-foreground">
                This score measures literal keyword fulfillment and structural ATS parseability,
                just like standard screening software.
              </p>
              <p className="text-muted-foreground mt-1.5">
                A high ATS score does not guarantee a recruiter interview. Use{" "}
                <strong>Resume Tailor</strong> for deep content refinement and contextually aligned
                bullet points to maximize your shortlisting chances.
              </p>
            </TooltipContent>
          </Tooltip>

          <Button size="sm" variant="outline" onClick={onReset} className="shrink-0 text-xs">
            Test Another Resume
          </Button>
        </div>
      </div>

      {/* Skills Matrix: Missing vs Matched */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Missing Must-Have Skills */}
        <div className="flex flex-col justify-between gap-3 rounded-xl border border-border bg-card p-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
                <XCircle className="size-4" aria-hidden="true" />
                Missing Must-Have Skills &amp; Keywords ({missing.length})
              </h4>
              <span className="text-[11px] text-muted-foreground">High ATS rejection risk</span>
            </div>

            {missing.length === 0 ? (
              <p className="py-2 text-[13px] text-emerald-500">
                None. You cover every required must-have keyword.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2 pt-1">
                {missing.map((skill, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    size="lg"
                    className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive sm:text-sm"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            )}

            {/* Missing Nice-To-Have */}
            {missingNiceToHave.length > 0 && (
              <div className="mt-3.5 pt-2.5 border-t border-border flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-muted-foreground">
                  Missing Nice-to-haves:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {missingNiceToHave.map((skill, i) => (
                    <span
                      key={i}
                      className="rounded-md bg-muted px-2.5 py-1 text-xs text-muted-foreground"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Matched Skills with Coss UI Tooltip Evidence Pills */}
        <div className="flex flex-col gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-1.5 text-sm font-semibold text-emerald-500">
              <CheckCircle2 className="size-4" />
              Matched Skills ({matched.length})
            </h4>
            <span className="text-xs text-muted-foreground">Hover/tap skill for evidence</span>
          </div>

          {matchedSkillsList.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">
              No direct skill matches detected in your resume text.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 pt-1">
              {matchedSkillsList.map((skillItem, i) => {
                const isSynonym =
                  skillItem.matchType === "synonym" || skillItem.matchType === "stem";

                return (
                  <Tooltip key={i}>
                    <TooltipTrigger
                      render={
                        <Badge
                          variant="secondary"
                          size="lg"
                          className={`cursor-pointer transition gap-1.5 py-1.5 px-3 text-xs sm:text-sm font-medium rounded-lg ${
                            isSynonym
                              ? "bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/40"
                              : "bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-500 border border-emerald-500/30"
                          }`}
                        />
                      }
                    >
                      {isSynonym ? (
                        <AlertCircle className="size-3.5 text-amber-700 dark:text-amber-400 shrink-0" />
                      ) : (
                        <Check className="size-3.5 text-emerald-500 shrink-0" />
                      )}
                      <span>{skillItem.name}</span>
                      {isSynonym && (
                        <span className="text-[11px] opacity-75 font-mono">(related)</span>
                      )}
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs p-1 text-sm flex flex-col gap-2 z-50">
                      {skillItem.section && (
                        <span className="text-sm font-medium capitalize">{skillItem.section}</span>
                      )}

                      {skillItem.snippet ? (
                        <p className="italic text-sm  font-serif">
                          &ldquo;{skillItem.snippet}…&rdquo;
                        </p>
                      ) : (
                        <p className="text-muted-foreground text-[11px]">
                          Found in section:{" "}
                          <span className="font-semibold capitalize text-foreground">
                            {skillItem.section}
                          </span>
                        </p>
                      )}

                      {isSynonym && skillItem.exactMatchWarning && (
                        <div className="rounded border border-amber-500/40 bg-amber-500/10 p-2 text-amber-700 dark:text-amber-400 text-[11px] leading-snug flex flex-col gap-1">
                          <span className="font-semibold flex items-center gap-1">
                            <AlertTriangle className="size-3 text-amber-700 dark:text-amber-400 shrink-0" />
                            ATS Scanner Warning:
                          </span>
                          <p className="opacity-95">{skillItem.exactMatchWarning}</p>
                          <button
                            type="button"
                            onClick={handleTailorClick}
                            className="mt-0.5 text-left font-semibold text-brand-foreground hover:underline cursor-pointer"
                          >
                            Use 1-Click Tailor to replace with exact keyword &rarr;
                          </button>
                        </div>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ATS Parseability Linter Box */}
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            {atsLinterResult.isAtsSafe ? (
              <div className="rounded-lg bg-green-500/10 p-2 shrink-0">
                <ShieldCheck className="size-6 text-emerald-500" />
              </div>
            ) : (
              <div className="rounded-lg bg-amber-500/10 p-2 shrink-0">
                <ShieldAlert className="size-6 text-amber-700 dark:text-amber-400" />
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-foreground">ATS Parseability Linter</h4>
              <span className="text-xs text-muted-foreground">
                Score: {atsLinterResult.score}/100 (
                {atsLinterResult.isAtsSafe
                  ? "Safe for Workday, Greenhouse & Taleo"
                  : "Format warnings detected"}
                )
              </span>
            </div>
          </div>

          {extractedResumeText && (
            <Dialog open={showAtsDialog} onOpenChange={setShowAtsDialog}>
              <DialogTrigger
                render={
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs gap-1.5 shrink-0 self-start sm:self-auto cursor-pointer"
                  />
                }
              >
                <Eye className="size-3.5 text-muted-foreground" />
                See ATS View
              </DialogTrigger>

              <DialogPopup className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <FileCode2 className="size-5 text-muted-foreground" />
                    How ATS Parsers Read Your Resume
                  </DialogTitle>
                  <DialogDescription className="text-xs sm:text-sm">
                    This is the exact raw linear text stream that Workday, Greenhouse, and Lever
                    extract from your file.
                  </DialogDescription>
                </DialogHeader>

                <DialogPanel>
                  <div className="flex flex-col gap-3">
                    <Accordion className="w-full rounded-lg border border-brand/20 bg-brand/5 px-3">
                      <AccordionItem value="ats-formatting-info" className="border-none">
                        <AccordionTrigger className="py-2.5 text-xs font-semibold text-foreground hover:no-underline">
                          Why does ATS linear formatting matter?
                        </AccordionTrigger>
                        <AccordionPanel className="text-xs text-muted-foreground leading-relaxed pt-0 pb-3">
                          Automated parsers read horizontally across lines. If your resume uses
                          multiple columns, tables, or complex formatting, text from separate
                          sections will get jumbled into unreadable fragments. JobCues single-column
                          Typst templates guarantee 100% linear parseability.
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>

                    <pre className="max-h-[50vh] overflow-y-auto rounded-xl border border-border bg-muted/50 p-3.5 font-mono text-sm text-foreground leading-relaxed whitespace-pre-wrap select-text">
                      {extractedResumeText}
                    </pre>
                  </div>
                </DialogPanel>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAtsDialog(false)}
                  >
                    Close
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowAtsDialog(false);
                      handleTailorClick();
                    }}
                    className="bg-primary hover:bg-primary/90 text-white text-xs font-semibold gap-1.5 cursor-pointer"
                  >
                    <Wand2 className="size-3.5" />
                    Convert to 100% ATS Safe Format
                  </Button>
                </DialogFooter>
              </DialogPopup>
            </Dialog>
          )}
        </div>

        {atsLinterResult.flags.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No layout or parseability defects found. Your resume formatting is clean and
            ATS-friendly.
          </p>
        ) : (
          <div className="flex flex-col gap-2 pt-1">
            {atsLinterResult.flags.map((flag, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2.5 rounded-lg border border-border bg-background/60 p-2.5 text-sm"
              >
                <AlertTriangle
                  className={`size-4 shrink-0 mt-0.5 ${
                    flag.severity === "error"
                      ? "text-destructive"
                      : "text-amber-700 dark:text-amber-400"
                  }`}
                />
                <div className="flex flex-col gap-0.5">
                  <span className="font-semibold text-foreground">{flag.title}</span>
                  <p className="text-muted-foreground text-xs leading-snug">{flag.message}</p>
                  <div className="flex items-center justify-between flex-wrap gap-2 mt-1">
                    <p className="text-brand-foreground text-xs font-semibold">
                      Fix: {flag.suggestion}
                    </p>
                    <button
                      type="button"
                      onClick={handleTailorClick}
                      className="text-xs font-semibold text-brand-foreground hover:underline cursor-pointer"
                    >
                      Fix with Resume Tailor &rarr;
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Conversion CTA Box (1-Click Optimization Hook) */}
      <div className="relative overflow-hidden rounded-xl border border-border bg-muted/40 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-brand-foreground">
              <Sparkles className="size-4 text-muted-foreground" />
              1-Click Resume Tailor
            </div>
            <h4 className="text-sm font-semibold text-foreground">
              {missing.length > 0
                ? `Add your ${missing.length} missing must-have keywords`
                : "Optimize My Resume for This Exact Role"}
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Let JobCues inject your missing must-have keywords organically into your experience
              bullets using proven ATS templates.
            </p>
          </div>

          <div className="flex w-full shrink-0 flex-col items-stretch gap-1.5 sm:w-auto sm:items-end">
            <Button
              size="lg"
              onClick={handleTailorClick}
              className="w-full shrink-0 cursor-pointer sm:w-auto"
            >
              <Wand2 className="size-4 mr-1.5" />
              Start Free &mdash; Tailor My Resume
              <ArrowRight className="size-4 ml-1" />
            </Button>
            <p className="text-center text-xs text-muted-foreground sm:text-right">
              3 free AI rewrites. No credit card.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
